import { createContext, useContext, useState, useCallback } from 'react';

const FeeContext = createContext();

const FEE_STORAGE_KEY = 'helix_fee_data';
const REGISTER_STORAGE_KEY = 'helix_register_data';
const VOUCHER_STORAGE_KEY = 'helix_voucher_data';
const RECEIPT_STORAGE_KEY = 'helix_receipt_data';
const STUDENT_REGISTRATION_STORAGE_KEY = 'helix_student_registration_data';

const getStoredItem = (key, fallback) => {
    if (typeof window === 'undefined') return fallback;
    try {
        const item = window.sessionStorage.getItem(key);
        return item ? JSON.parse(item) : fallback;
    } catch (e) {
        console.warn(`Error reading ${key} from sessionStorage`, e);
        return fallback;
    }
};

const setStoredItem = (key, value) => {
    if (typeof window === 'undefined') return;
    try {
        if (value === null || value === undefined) {
            window.sessionStorage.removeItem(key);
        } else {
            window.sessionStorage.setItem(key, JSON.stringify(value));
        }
    } catch (e) {
        console.warn(`Error writing ${key} to sessionStorage`, e);
    }
};

const initialFeeState = {
    id: null,
    mode: null,
    firstname: '',
    lastname: '',
    courseId: '',
    courseType: '',
    coursename: '',
    studentId: '',
    studentemail: '',
    studentphone: '',
    studentname: '',
    lumpsumAmount: '',
    totalAmount: '',
    referenceNo: '',
    transaction_id: '',
    paymentStatus: '',
    is_student: true,
    isLumpsumEnabled: false,
    handleSubmitCallback: null,
    selectedInstallments: [],
    installmentsData: null,
};

export function FeeProvider({ children }) {
    const [feeData, setFeeData] = useState(() => {
        const stored = getStoredItem(FEE_STORAGE_KEY, null);
        return stored ? { ...initialFeeState, ...stored } : initialFeeState;
    });

    const [feeReceiptUrl, setFeeReceiptUrl] = useState(() => {
        return getStoredItem(RECEIPT_STORAGE_KEY, { url: '' });
    });

    const initialRegisterState = {
        exam_id: '',
        first_name: '',
        last_name: '',
        father_name: '',
        gender: '',
        dob: null,
        attending_class: '',
        desired_stream: '',
        school_name: '',
        school_city: '',
        address: '',
        zip_code: '',
        city: '',
        state: '',
        phone_number: '',
        email: '',
        nearest_city_1: '',
        nearest_city_2: '',
        payment_amount: '',
        username: '',
        payment_status: '',
        is_email_verified: false,
    };

    const [registerData, setRegisterData] = useState(() => {
        return getStoredItem(REGISTER_STORAGE_KEY, initialRegisterState);
    });

    const [cashPaymentStatus, setCashPaymentStatus] = useState({
        isSuccessful: false,
        timestamp: null,
        amount: 0,
        mode: null,
        ackPdfUrl: null,
    });

    // NEW: Function to set cash payment success
    const setCashPaymentSuccess = (isSuccessful, amount = 0, mode = null, ackPdfUrl = null) => {
        setCashPaymentStatus({
            isSuccessful,
            timestamp: isSuccessful ? new Date().toISOString() : null,
            amount: isSuccessful ? amount : 0,
            mode: isSuccessful ? mode : null,
            ackPdfUrl: isSuccessful ? ackPdfUrl : null,
        });
    };

    // NEW: Function to check if cash payment was successful
    const isCashPaymentSuccessful = () => {
        return cashPaymentStatus.isSuccessful;
    };

    // NEW: Function to get cash payment details
    const getCashPaymentDetails = () => {
        return cashPaymentStatus;
    };

    // NEW: Function to reset cash payment status
    const resetCashPaymentStatus = useCallback(() => {
        setCashPaymentStatus({
            isSuccessful: false,
            timestamp: null,
            amount: 0,
            mode: null,
            ackPdfUrl: null,
        });
    }, []);

    // In FeeContext.js, add this function before the return statement
    const clearAllData = useCallback(() => {
        setStoredItem(FEE_STORAGE_KEY, null);
        setStoredItem(REGISTER_STORAGE_KEY, null);
        setStoredItem(VOUCHER_STORAGE_KEY, null);
        setStoredItem(RECEIPT_STORAGE_KEY, null);
        setStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, null);

        setFeeData(initialFeeState);
        setFeeReceiptUrl({
            url: ''
        });
        setRegisterData(initialRegisterState);
        setStudentRegistrationData(null);
        setStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, null);
        setVoucherNo(null);
        setCashRegisterData(null);
        resetCashPaymentStatus();
    }, [resetCashPaymentStatus]);


    // NEW: Store complete student registration data
    const [studentRegistrationData, setStudentRegistrationData] = useState(() => {
        return getStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, null);
    });

    const [voucherNo, setVoucherNo] = useState(() => {
        return getStoredItem(VOUCHER_STORAGE_KEY, null);
    });
    const [cashRegisterData, setCashRegisterData] = useState(null);

    const registrationUrlTemplates = (isLumpsumEnabled) => ({
        'talent hunt': `${process.env.REACT_APP_HOST_API}/talent-hunt/api/talent-hunt-exams/register-student/`,
        'Offline': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/fee-payment/`,
        'offline': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/fee-payment/`,

        'Online': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/fee-payment/`,
        'online': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/fee-payment/`,
        'registration': `${process.env.REACT_APP_HOST_API}/student-registration/api/register/`,
        'newstudent': `${process.env.REACT_APP_HOST_API}/student-registration/api/students/signup/temp-student/`,
        'hostel': `${process.env.REACT_APP_HOST_API}/hostel/api/hostel/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'security': `${process.env.REACT_APP_HOST_API}/hostel/api/hostel/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'mess': `${process.env.REACT_APP_HOST_API}/hostel/api/mess/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`

    });

    const getStudentParentPayloadOverrides = () => {
        const isStudent = feeData?.is_student ?? feeData?.installmentsData?.is_student;
        const isParent = feeData?.is_parent ?? feeData?.isParent ?? feeData?.installmentsData?.is_parent ?? feeData?.installmentsData?.isParent;
        const ignorePreprinted = feeData?.ignore_preprinted ?? feeData?.installmentsData?.ignore_preprinted;

        if (isStudent || isParent || ignorePreprinted) {
            return {
                is_student: isStudent !== undefined ? isStudent : !isParent,
                is_parent: Boolean(isParent),
                isParent: Boolean(isParent),
                ignore_preprinted: true,
                print_on_preprinted: false,
                PRINT_ON_PREPRINTED_PDF: false,
            };
        }
        return {};
    };

    const registrationPayloadTemplates = (isLumpsumEnabled, paymentMethod, processedTotalAmount, selectedInstallments) => ({
        'talent hunt': (referenceNo, transactionId) => ({
            exam_id: registerData.exam_id,
            first_name: registerData.first_name,
            last_name: registerData.last_name,
            father_name: registerData.father_name || '',
            gender: registerData.gender,
            dob: registerData.dob,
            attending_class: registerData.attending_class,
            desired_stream: registerData.desired_stream,
            school_name: registerData.school_name,
            school_city: registerData.school_city,
            address: registerData.address,
            zip_code: registerData.zip_code,
            city: registerData.city,
            state: registerData.state,
            phone_number: registerData.phone_number,
            email: registerData.email,
            nearest_city_1: registerData.nearest_city_1,
            nearest_city_2: registerData.nearest_city_2,
            payment_amount: registerData.payment_amount,
            username: registerData.username,
            payment_status: registerData.payment_status,
            is_email_verified: Boolean(registerData.is_email_verified),
            transaction_id: transactionId || referenceNo,
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            ...getStudentParentPayloadOverrides(),
        }),

        'Online': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
            ...getStudentParentPayloadOverrides(),
        }),
        'online': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
            ...getStudentParentPayloadOverrides(),
        }),
        'Offline': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
            ...getStudentParentPayloadOverrides(),
        }),
        'offline': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
            ...getStudentParentPayloadOverrides(),
        }),
        'hostel': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
        }),
        'security': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
        }),
        'mess': (referenceNo, transactionId) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: referenceNo || feeData?.referenceNo,
            transaction_id: transactionId,
        }),
        // Add student registration payload that uses stored data
        'registration': (referenceNo, transactionId) => {
            if (!studentRegistrationData) return {};

            const payload = {
                // Step 1 data
                avatar_image: studentRegistrationData.avatar_image,
                first_name: studentRegistrationData.first_name,
                last_name: studentRegistrationData.last_name,
                full_name: studentRegistrationData.full_name,
                state: studentRegistrationData.state,
                city: studentRegistrationData.city,
                address: studentRegistrationData.address,
                zip_code: studentRegistrationData.zip_code,
                phone_number: studentRegistrationData.phone_number,
                whatsapp_number: studentRegistrationData.whatsapp_number,
                email: studentRegistrationData.email,
                gender: studentRegistrationData.gender,
                dob: studentRegistrationData.dob ? new Date(studentRegistrationData.dob).toISOString().split('T')[0] : null,
                signature: studentRegistrationData.signature,

                // Step 2 data
                father_name: studentRegistrationData.father_name,
                mother_name: studentRegistrationData.mother_name,
                father_mobile_number: studentRegistrationData.father_mobile_number,
                mother_mobile_number: studentRegistrationData.mother_mobile_number,
                father_occupation: studentRegistrationData.father_occupation,
                parent_email: studentRegistrationData.parent_email,

                // Step 3 data
                course_name: studentRegistrationData.course_name?.id || '',
                course_center: studentRegistrationData.course_center?.id || '',
                course_type: studentRegistrationData.course_type,

                // Add specific course IDs
                ...(studentRegistrationData.course_type === 'online'
                    ? { online_course_id: studentRegistrationData.course_name?.id || '' }
                    : { offline_course_id: studentRegistrationData.course_name?.id || '' }
                ),
                amount: processedTotalAmount || feeData?.amount || feeData?.totalAmount,
                payment_mode: paymentMethod,
                reference_no: referenceNo || feeData?.referenceNo,
                transaction_id: transactionId,
            };

            return payload;
        },
        'newstudent': (referenceNo, transactionId) => {
            return prepareStudentFormData(feeData.courseType);
        }
    });

    const paymentUrlTemplates = (isLumpsumEnabled) => ({
        'talent hunt': `${process.env.REACT_APP_HOST_API}/talent-hunt/api/payment/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'Offline': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'offline': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,

        'Online': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'online': `${process.env.REACT_APP_HOST_API}/courses/api/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,

        'hostel': `${process.env.REACT_APP_HOST_API}/hostel/api/hostel/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'security': `${process.env.REACT_APP_HOST_API}/hostel/api/hostel/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,
        'mess': `${process.env.REACT_APP_HOST_API}/hostel/api/mess/${isLumpsumEnabled ? 'lumpsum' : 'installment'}/`,

        // Add student registration URL
        'registration': `${process.env.REACT_APP_HOST_API}/registration/api/register/`,
        'newstudent': `${process.env.REACT_APP_HOST_API}/student-registration/api/students/signup/temp-student/`,

        // 'newstudent': `/eazypay/api/student/registration/initiate_payment/`,
    });

    const paymentPayloadTemplates = {
        'talent hunt': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            exam_id: feeData?.id,
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || [Number(feeData?.id)],
            ...getStudentParentPayloadOverrides(),
        }),
        'Online': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
            ...getStudentParentPayloadOverrides(),
        }),
        'online': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
            ...getStudentParentPayloadOverrides(),
        }),
        'Offline': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
            ...getStudentParentPayloadOverrides(),
        }),
        'offline': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            course_id: feeData?.courseId || feeData?.installmentsData?.course_id,
            student: feeData?.studentId || feeData?.installmentsData?.student_id,
            installments: isLumpsumEnabled ? [] : selectedInstallments || feeData?.selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
            ...getStudentParentPayloadOverrides(),
        }),
        'hostel': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments, cashRegisterData) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            cash_register_id: cashRegisterData ? cashRegisterData.id : 0,
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
        }),
        'security': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments, cashRegisterData) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            cash_register_id: cashRegisterData ? cashRegisterData.id : 0,
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
        }),
        'mess': (isLumpsumEnabled, processedTotalAmount, paymentMethod, selectedInstallments, cashRegisterData) => ({
            voucher_number: voucherNo ? voucherNo.voucher_number : '',
            cash_register_id: cashRegisterData ? cashRegisterData.id : 0,
            amount: parseFloat(processedTotalAmount),
            payment_mode: paymentMethod,
            installments: isLumpsumEnabled
                ? [feeData?.installmentsData?.installments[0]?.installment_id].filter(Boolean)
                : selectedInstallments || [Number(feeData?.id)],
            reference_no: feeData?.referenceNo,
            transaction_id: feeData?.transaction_id,
        }),
    };

    const updateFeeData = (newData) => {
        setFeeData((prevData) => {
            const incoming = typeof newData === 'function' ? newData(prevData) : (newData || {});
            const merged = { ...prevData };

            Object.keys(incoming).forEach((key) => {
                if (incoming[key] !== undefined) {
                    merged[key] = incoming[key];
                }
            });

            // If installmentsData has student or course info, backfill missing top-level feeData fields
            const inst = merged.installmentsData;
            if (inst) {
                if (!merged.studentname && inst.student_name) merged.studentname = inst.student_name;
                if (!merged.studentemail && inst.student_email) merged.studentemail = inst.student_email;
                if (!merged.studentId && inst.student_id) merged.studentId = inst.student_id;
                if (!merged.courseId && inst.course_id) merged.courseId = inst.course_id;
                if (!merged.coursename && inst.course_name) merged.coursename = inst.course_name;
                if (!merged.courseType && inst.course_mode) merged.courseType = inst.course_mode;
                if (!merged.mode && inst.course_mode) merged.mode = inst.course_mode.toLowerCase();
            }

            try {
                const { handleSubmitCallback, ...storable } = merged;
                setStoredItem(FEE_STORAGE_KEY, storable);
            } catch (e) {
                console.warn('Failed to persist feeData to sessionStorage', e);
            }

            return merged;
        });
    };

    const updateFeeReceiptUrl = (url) => {
        const val = typeof url === 'object' ? url : { url };
        setFeeReceiptUrl(val);
        setStoredItem(RECEIPT_STORAGE_KEY, val);
    };

    const updateRegisterData = (
        exam_id,
        first_name,
        last_name,
        father_name,
        gender,
        dob,
        attending_class,
        desired_stream,
        school_name,
        school_city,
        address,
        zip_code,
        city,
        state,
        phone_number,
        email,
        nearest_city_1,
        nearest_city_2,
        payment_amount,
        username,
        payment_status,
        is_email_verified = false
    ) => {
        let newRegister;
        if (typeof exam_id === 'object' && exam_id !== null) {
            newRegister = {
                ...initialRegisterState,
                ...exam_id,
                is_email_verified: Boolean(exam_id.is_email_verified),
            };
        } else {
            newRegister = {
                exam_id: exam_id || '',
                first_name: first_name || '',
                last_name: last_name || '',
                father_name: father_name || '',
                gender: gender || '',
                dob: dob || null,
                attending_class: attending_class || '',
                desired_stream: desired_stream || '',
                school_name: school_name || '',
                school_city: school_city || '',
                address: address || '',
                zip_code: zip_code || '',
                city: city || '',
                state: state || '',
                phone_number: phone_number || '',
                email: email || '',
                nearest_city_1: nearest_city_1 || '',
                nearest_city_2: nearest_city_2 || '',
                payment_amount: payment_amount || '',
                username: username || '',
                payment_status: payment_status || '',
                is_email_verified: Boolean(is_email_verified),
            };
        }
        setRegisterData(newRegister);
        setStoredItem(REGISTER_STORAGE_KEY, newRegister);
    };

    // NEW: Function to store complete student registration data
    const setStudentRegistration = (data) => {
        setStudentRegistrationData(data);
        if (data) {
            try {
                const serializableData = { ...data };
                delete serializableData.avatar_image;
                delete serializableData.signature;
                setStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, serializableData);
            } catch (err) {
                console.warn('Failed to store student registration data in sessionStorage', err);
            }
        } else {
            setStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, null);
        }
    };

    // NEW: Function to clear student registration data
    const clearStudentRegistration = () => {
        setStudentRegistrationData(null);
        setStoredItem(STUDENT_REGISTRATION_STORAGE_KEY, null);
    };

    // NEW: Helper to prepare FormData from stored student registration data
    // In your FeeContext.js, update the prepareStudentFormData function:
    const prepareStudentFormData = (courseType) => {
        if (!studentRegistrationData) {
            console.warn('No student registration data found');
            return new FormData();
        }

        const formData = new FormData();
        const data = studentRegistrationData;

        // STEP 1
        if (data.avatar_image) formData.append("avatar_image", data.avatar_image);
        if (data.first_name) formData.append("first_name", data.first_name);
        if (data.last_name) formData.append("last_name", data.last_name);
        if (data.full_name) formData.append("full_name", data.full_name);
        if (data.state) formData.append("state", data.state);
        if (data.city) formData.append("city", data.city);
        if (data.address) formData.append("address", data.address);
        if (data.zip_code) formData.append("zip_code", data.zip_code);
        if (data.phone_number) formData.append("phone_number", data.phone_number);
        if (data.whatsapp_number) formData.append("whatsapp_number", data.whatsapp_number);
        if (data.email) formData.append("email", data.email);
        if (data.gender) formData.append("gender", data.gender);
        if (data.category) formData.append("category", data.category);
        if (data.dob) {
            const dobDate = new Date(data.dob);
            const formattedDob = dobDate.toISOString().split('T')[0];
            formData.append("dob", formattedDob);
        }
        if (data.signature) formData.append("signature", data.signature);

        // STEP 2 
        if (data.father_name) formData.append("father_name", data.father_name);
        if (data.mother_name) formData.append("mother_name", data.mother_name);
        if (data.father_mobile_number) formData.append("father_mobile_number", data.father_mobile_number);
        if (data.mother_mobile_number) formData.append("mother_mobile_number", data.mother_mobile_number);
        if (data.father_occupation) formData.append("father_occupation", data.father_occupation);
        if (data.parent_email) formData.append("parent_email", data.parent_email);

        if (data.current_qualification) formData.append("current_qualification", data.current_qualification);
        if (data.last_school_name) formData.append("last_school_name", data.last_school_name);
        if (data.last_school_address) formData.append("last_school_address", data.last_school_address);
        if (data.education_board) formData.append("education_board", data.education_board);
        if (data.grade_system) formData.append("marks_format", data.grade_system);
        if (data.percentage) formData.append("marks", data.percentage);
        if (data.grade) formData.append("marks", data.grade);
        if (data.cgpa) formData.append("marks", data.cgpa);
        if (data.entrance_exam === 'yes' && data.entrance_exam_name === 'JEE') {
            formData.append("jee_attempted", true);
            formData.append("jee_marks", data.entrance_exam_score);
        }
        if (data.entrance_exam === 'yes' && data.entrance_exam_name === 'NEET') {
            formData.append("neet_attempted", true);
            formData.append("neet_marks", data.entrance_exam_score);
        }
        if (data.talent_hunt_exam === 'yes') {
            formData.append("talent_hunt_exam", true);
            formData.append("roll_no", data.score || '');
            formData.append("score", data.roll_no || '');
            formData.append("scholarship", data.scholarship || '');
        }

        const courseTypeToUse = courseType || data.course_type;
        if (courseTypeToUse) {
            formData.append("course_type", courseTypeToUse);
        }

        // STEP 3 - Add course ID based on course type
        if (data.course_name && data.course_name.id) {
            formData.append("course_name", data.course_name.id);

            if (courseTypeToUse === "offline") {
                formData.append("offline_course_id", data.course_name.id);
                // Also use offline_course_id from data if available
                if (data.offline_course_id) {
                    formData.append("offline_course_id", data.offline_course_id);
                }
            } else if (courseTypeToUse === "online") {
                formData.append("online_course_id", data.course_name.id);
                // Also use online_course_id from data if available
                if (data.online_course_id) {
                    formData.append("online_course_id", data.online_course_id);
                }
            }
        }

        if (data.course_center && data.course_center.id) {
            formData.append("course_center", data.course_center.id);
        }

        if (data.tax_id) {
            formData.append("tax_id", data.tax_id);
        }

        if (data.category) {
            formData.append("category", data.category);
        }
        if (data.current_qualification) {
            formData.append("current_qualification", data.current_qualification);
        }
        if (data.last_school_name) {
            formData.append("last_school_name", data.last_school_name);
        }
        if (data.last_school_address) {
            formData.append("last_school_address", data.last_school_address);
        }
        if (data.education_board) {
            formData.append("education_board", data.education_board);
        }
        if (data.grade_system) {
            formData.append("marks_format", data.grade_system);
        }
        if (data.percentage) {
            formData.append("marks", data.percentage);
        }
        if (data.grade) {
            formData.append("marks", data.grade);
        }
        if (data.cgpa) {
            formData.append("marks", data.cgpa);
        }
        if (data.entrance_exam === 'yes' && data.entrance_exam_name === 'JEE') {
            formData.append("jee_attempted", true);
            formData.append("jee_marks", data.entrance_exam_score);
        }
        if (data.entrance_exam === 'yes' && data.entrance_exam_name === 'NEET') {
            formData.append("neet_attempted", true);
            formData.append("neet_marks", data.entrance_exam_score);
        }
        if (data.talent_hunt_exam === 'yes') {
            formData.append("talent_hunt_exam", true);
            formData.append("roll_no", data.score || '');
            formData.append("score", data.roll_no || '');
            formData.append("scholarship", data.scholarship || '');
        }

        if (data.installment_data) {
            const installmentData = typeof data.installment_data === 'string'
                ? data.installment_data
                : JSON.stringify(data.installment_data);
            formData.append('installment_data', installmentData);
        }

        return formData;
    };

    // NEW: Helper to get student registration data as JSON (without files)
    const getStudentRegistrationJSON = () => {
        if (!studentRegistrationData) return null;

        const data = { ...studentRegistrationData };

        // Convert date to string format
        if (data.dob) {
            data.dob = new Date(data.dob).toISOString().split('T')[0];
        }

        // Handle file objects (you might want to remove them for JSON)
        delete data.avatar_image;
        delete data.signature;

        return data;
    };

    const updateVoucherNo = (voucher) => {
        setVoucherNo(voucher);
        setStoredItem(VOUCHER_STORAGE_KEY, voucher);
    };

    const updateCashRegisterData = (data) => {
        setCashRegisterData(data);
    };

  

    return (
        <FeeContext.Provider value={{
            feeData,
            updateFeeData,
            registerData,
            updateRegisterData,
            updateFeeReceiptUrl,
            feeReceiptUrl,
            registrationUrlTemplates,
            registrationPayloadTemplates,
            paymentUrlTemplates,
            paymentPayloadTemplates,
            voucherNo,
            updateVoucherNo,
            cashRegisterData,
            updateCashRegisterData,
            // cleardarta
            clearAllData,
            // NEW: Student registration functions
            studentRegistrationData,
            setStudentRegistration,
            clearStudentRegistration,
            prepareStudentFormData,
            getStudentRegistrationJSON,
            // NEW: Cash payment status functions
            cashPaymentStatus,
            setCashPaymentSuccess,
            isCashPaymentSuccessful,
            getCashPaymentDetails,
            resetCashPaymentStatus,
        }}>
            {children}
        </FeeContext.Provider>
    );
}

export function useFee() {
    return useContext(FeeContext);
}
